import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import LoaderOlimpo from "@/components/LoaderOlimpo";
import AvisoWindows from "@/components/AvisoWindows";

export default function Perfil() {
  const nav = useNavigate();
  const { user, empresa, refreshMe } = useAuth();
  const [perfil, setPerfil] = useState(null);
  const [resumen, setResumen] = useState("");
  const [editRes, setEditRes] = useState(false);
  const [editDol, setEditDol] = useState(false);
  const [doloresText, setDoloresText] = useState("");
  const [nivel, setNivel] = useState("");
  const [so, setSo] = useState("");
  const [avisoWin, setAvisoWin] = useState(false);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/perfil/mio");
      if (!data.perfil) { nav("/inicio"); return; }
      setPerfil(data.perfil);
      setResumen(data.perfil.resumen || "");
      setNivel(user?.nivel_tecnologico || "");
      setSo(user?.sistema_operativo || "");
    })();
  }, [nav, user]);

  const guardarResumen = async () => {
    setEditRes(false);
    const { data } = await api.patch("/perfil/mio", { resumen });
    setPerfil(data.perfil);
  };

  const abrirEditDolores = () => {
    setDoloresText((perfil?.dolores || []).join("\n"));
    setEditDol(true);
  };

  const guardarDolores = async () => {
    setEditDol(false);
    const arr = doloresText.split("\n").map((s) => s.trim()).filter(Boolean);
    const { data } = await api.patch("/perfil/mio", { dolores: arr });
    setPerfil(data.perfil);
  };

  const guardarNivel = async (v) => {
    setNivel(v);
    await api.post("/preparar", { datos: { nivel_tecnologico: v } });
    if (refreshMe) refreshMe();
  };

  const guardarSo = async (v) => {
    setSo(v);
    await api.post("/preparar", { datos: { sistema_operativo: v, plataforma_trabajo: "computadora" } });
    if (refreshMe) refreshMe();
  };

  // Windows: si todavía no eligió, abrimos el pop-up antes de guardar (una vez por cuenta).
  const pedirWindows = () => {
    if (user?.decision_so) { guardarSo("windows"); return; }
    setAvisoWin(true);
  };
  const resolverAvisoWin = (dec) => {
    setAvisoWin(false);
    guardarSo(dec === "uso_mac" ? "mac" : "windows");
  };

  if (!perfil) return <LoaderOlimpo fullscreen />;

  const herramientas = perfil.herramientas || [];
  const dolores = perfil.dolores || [];

  const eyebrow = "mono-label text-dorado mb-3";
  const empresaLinea = [
    empresa?.nombre,
    empresa?.rubro,
    empresa?.tamano ? `${empresa.tamano} personas` : null,
    empresa?.pais || empresa?.ciudad,
  ].filter(Boolean).join(" · ");

  return (
    <div className="min-h-screen bg-negro px-6 md:px-12 py-16">
      <div className="max-w-4xl mx-auto">
        <motion.h1 initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="titulo text-4xl md:text-5xl mb-2">
          Listo. Esto es lo que entendí de vos.
        </motion.h1>
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5, delay: 0.15 }} className="text-gris mb-10">
          Este es el mapa de lo que me contaste.
        </motion.p>

        {/* EL MAPA — grilla alineada, espacios parejos, jerarquía título/contenido */}
        <div className="grid md:grid-cols-2 gap-4 items-stretch">
          {/* QUIÉN SOS — ancho completo */}
          <motion.div className="md:col-span-2" initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0 }}>
            <div className="card-dark p-6 h-full">
              <div className="flex items-center justify-between mb-3">
                <div className="mono-label text-dorado">QUIÉN SOS</div>
                {!editRes && (
                  <button onClick={() => setEditRes(true)} className="mono-label text-gris hover:text-dorado transition-colors" data-testid="resumen-editar">EDITAR</button>
                )}
              </div>
              {editRes ? (
                <>
                  <textarea autoFocus className="input-dark min-h-[100px]" value={resumen} onChange={(e) => setResumen(e.target.value)} onBlur={guardarResumen} data-testid="edit-resumen" />
                  <p className="text-gris text-xs mt-2">Se guarda solo al salir del cuadro.</p>
                </>
              ) : (
                <p className="text-crema leading-relaxed text-lg" data-testid="ver-resumen">{perfil.resumen}</p>
              )}
            </div>
          </motion.div>

          {/* TU EMPRESA */}
          <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.1 }}>
            <div className="card-dark p-6 h-full">
              <div className={eyebrow}>TU EMPRESA</div>
              <p className="text-crema text-lg leading-relaxed">{empresaLinea || "—"}</p>
            </div>
          </motion.div>

          {/* CÓMO TRABAJÁS */}
          <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.2 }}>
            <div className="card-dark p-6 h-full">
              <div className={eyebrow}>CÓMO TRABAJÁS</div>
              <div className="flex flex-wrap gap-2">
                {herramientas.length === 0 && <span className="text-gris text-sm">Todavía no detecté herramientas.</span>}
                {herramientas.map((h, i) => (
                  <span key={i} className="mono-label text-crema border border-borde rounded-lg px-3 py-1.5" data-testid={`herramienta-${i}`}>
                    {(typeof h === "object" ? h.nombre : h)}
                  </span>
                ))}
              </div>
            </div>
          </motion.div>

          {/* DÓNDE SE TE VA EL TIEMPO — ancho completo, editable */}
          <motion.div className="md:col-span-2" initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.3 }}>
            <div className="card-dark p-6 h-full">
              <div className="flex items-center justify-between mb-3">
                <div className="mono-label text-dorado">DÓNDE SE TE VA EL TIEMPO</div>
                {!editDol && (
                  <button onClick={abrirEditDolores} className="mono-label text-gris hover:text-dorado transition-colors" data-testid="dolores-editar">EDITAR</button>
                )}
              </div>
              {editDol ? (
                <>
                  <textarea
                    autoFocus
                    className="input-dark min-h-[140px]"
                    value={doloresText}
                    onChange={(e) => setDoloresText(e.target.value)}
                    onBlur={guardarDolores}
                    placeholder="Una línea por cada cosa que te come tiempo. Escribí como quieras."
                    data-testid="dolores-edit"
                  />
                  <p className="text-gris text-xs mt-2">Una línea por cada punto. Se guarda solo al salir del cuadro.</p>
                </>
              ) : dolores.length > 0 ? (
                <ul className="space-y-3">
                  {dolores.map((d, i) => (
                    <li key={i} className="text-crema leading-relaxed flex gap-3" data-testid={`dolor-${i}`}>
                      <span className="text-dorado mt-1 shrink-0">·</span>
                      <span>{d}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-gris text-sm" data-testid="dolores-vacio">
                  Todavía no anoté nada acá. Tocá EDITAR para sumarlo.
                </p>
              )}
            </div>
          </motion.div>
        </div>

        {/* PREGUNTAS NUESTRAS — fuera del mapa, obligatorias para seguir */}
        <motion.div
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.45 }}
          className="mt-10 pt-10"
          style={{ borderTop: "1px solid #262626" }}
          data-testid="perfil-nivel-seccion"
        >
          <div className="mono-label text-gris mb-5">DOS COSAS ANTES DE SEGUIR</div>

          <h3 className="font-serif text-crema mb-1" style={{ fontSize: 26, lineHeight: 1.2 }}>¿Cómo te llevás con la tecnología?</h3>
          <p className="text-gris text-sm mb-5">Con esto ajusto cuánto te explico en cada paso. Podés cambiarlo cuando quieras.</p>
          <div className="flex flex-wrap gap-2 mb-10" data-testid="perfil-nivel">
            {[{ v: "bien", label: "Me manejo bien" }, { v: "mas_o_menos", label: "Más o menos" }, { v: "cuesta", label: "Me cuesta, explicame todo" }].map((o) => (
              <button key={o.v} onClick={() => guardarNivel(o.v)} data-testid={`perfil-nivel-${o.v}`}
                className={`btn-fantasma text-sm ${nivel === o.v ? "border-dorado text-dorado" : ""}`}>{o.label}</button>
            ))}
          </div>

          <h3 className="font-serif text-crema mb-1" style={{ fontSize: 26, lineHeight: 1.2 }}>¿Qué computadora usás?</h3>
          <p className="text-gris text-sm mb-5">Con esto, cada paso te muestra directamente el instructivo y el video de tu sistema, sin volver a preguntarte.</p>
          <div className="flex flex-wrap gap-2" data-testid="perfil-so">
            {[{ v: "mac", label: "Mac" }, { v: "windows", label: "Windows" }].map((o) => (
              <button key={o.v} onClick={() => (o.v === "windows" ? pedirWindows() : guardarSo(o.v))} data-testid={`perfil-so-${o.v}`}
                className={`btn-fantasma text-sm ${so === o.v ? "border-dorado text-dorado" : ""}`}>{o.label}</button>
            ))}
          </div>
        </motion.div>

        <AvisoWindows open={avisoWin} onResolver={resolverAvisoWin} />

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7 }} className="mt-12">
          <div
            className="rounded-xl p-6 mb-8"
            style={{ border: "1px solid rgba(201,168,76,0.45)", background: "rgba(201,168,76,0.05)" }}
            data-testid="perfil-tomate-tiempo"
          >
            <p className="text-crema leading-relaxed">
              Con lo que me contaste ya armé esto.
            </p>
            <p className="text-crema leading-relaxed mt-3">
              Lo que sigue es la parte donde tu tiempo más rinde: cuanto más me contás, menos genérico es todo lo que armemos después.
            </p>
            <p className="text-gris leading-relaxed mt-3">
              Y no te preocupes por decirlo bien. Hablá desordenado, como se te venga, sin pensarlo. El desorden me sirve igual.
            </p>
          </div>
          {!(nivel && so) && (
            <p className="text-gris text-sm mb-3" data-testid="perfil-falta-responder">
              Respondé las dos preguntas de arriba para seguir.
            </p>
          )}
          <button
            className="btn-dorado disabled:opacity-40 disabled:cursor-not-allowed"
            disabled={!(nivel && so)}
            onClick={() => { if (nivel && so) nav("/bienvenida-video"); }}
            data-testid="perfil-seguir"
          >
            ESTÁ BIEN, SEGUIMOS
          </button>
        </motion.div>
      </div>
    </div>
  );
}
